import { Component, OnInit, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { ConciergeService, ChatMessage } from '../../core/services/concierge.service';

/**
 * Parsed message with content and optional doc links
 */
interface ParsedMessage {
  content: SafeHtml;
  docLinks: { title: string; url: string }[];
}

/**
 * Floating chat widget for AI Concierge.
 * Provides real-time chat interface with the AI assistant.
 */
@Component({
  selector: 'app-concierge-chat',
  standalone: false,
  templateUrl: './concierge-chat.component.html',
  styleUrl: './concierge-chat.component.scss'
})
export class ConciergeChatComponent implements OnInit, OnDestroy {
  @ViewChild('messageContainer') messageContainer!: ElementRef;
  @ViewChild('inputField') inputField!: ElementRef;

  isOpen = false;
  isLoading = false;
  userInput = '';
  messages: ChatMessage[] = [];
  error: string | null = null;

  private subscriptions: Subscription[] = [];
  private parsedMessagesCache = new Map<string, ParsedMessage>();

  constructor(
    private conciergeService: ConciergeService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    // Subscribe to messages
    this.subscriptions.push(
      this.conciergeService.messages$.subscribe(messages => {
        const previousCount = this.messages.length;
        const newMessage = messages[messages.length - 1];
        const isNewAssistantMessage = messages.length > previousCount && newMessage?.role === 'assistant';

        this.messages = messages;
        this.cdr.markForCheck();

        if (isNewAssistantMessage) {
          // Wait for Angular's change detection cycle to fully complete (including removal of
          // the loading indicator) before measuring DOM positions. Any earlier measurement
          // includes the loading indicator height and causes scrollTop to overshoot, which
          // the browser clamps to max — resulting in the bottom of the answer being visible.
          setTimeout(() => this.scrollToAnswerStart(), 0);
        } else {
          // User message or initial load: scroll to bottom to show the loading indicator.
          setTimeout(() => this.scrollToBottom(), 0);
        }
      })
    );

    // Subscribe to loading state
    this.subscriptions.push(
      this.conciergeService.isLoading$.subscribe(loading => {
        this.isLoading = loading;
        this.cdr.markForCheck();
      })
    );

    // Subscribe to open state
    this.subscriptions.push(
      this.conciergeService.isOpen$.subscribe(open => {
        this.isOpen = open;
        if (open) {
          setTimeout(() => {
            this.scrollToBottom();
            this.focusInput();
          }, 100);
        }
        this.cdr.markForCheck();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Toggle the chat widget open/closed.
   */
  toggleChat(): void {
    this.conciergeService.toggleChat();
  }

  /**
   * Send the current message.
   */
  sendMessage(): void {
    const message = this.userInput.trim();
    if (!message || this.isLoading) return;

    this.error = null;
    this.userInput = '';
    this.conciergeService.addUserMessage(message);

    this.conciergeService.sendMessage(message).subscribe({
      next: (response) => {
        this.conciergeService.addAssistantMessage(response.message);
      },
      error: (err) => {
        this.error = err.message || 'Failed to send message. Please try again.';
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Clear the current conversation.
   */
  clearChat(): void {
    this.conciergeService.clearConversation().subscribe({
      error: () => {
        // Clear locally even if server call fails
        this.error = null;
      }
    });
    this.error = null;
  }

  /**
   * Handle keyboard events in the input field.
   */
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  /**
   * Scroll the message container to the bottom.
   */
  private scrollToBottom(): void {
    if (this.messageContainer) {
      const el = this.messageContainer.nativeElement;
      el.scrollTop = el.scrollHeight;
    }
  }

  /**
   * Scroll so the TOP of the last message is visible.
   * Called after a new assistant answer is rendered.
   *
   * getBoundingClientRect() gives the element's current position in the viewport.
   * The delta between the message top and the container top tells us exactly how
   * much to adjust scrollTop — no dependency on offsetParent or padding math.
   */
  private scrollToAnswerStart(): void {
    if (!this.messageContainer) return;

    const container = this.messageContainer.nativeElement;
    const allMessages = container.querySelectorAll('.concierge-message');
    const lastMessage = allMessages[allMessages.length - 1] as HTMLElement;

    if (!lastMessage) return;

    const containerTop = container.getBoundingClientRect().top;
    const messageTop = lastMessage.getBoundingClientRect().top;

    // Positive delta: message is below container top → scroll down
    // Negative delta: message top is above container top → scroll up
    container.scrollTop += messageTop - containerTop;
  }


  /**
   * Focus the input field.
   */
  private focusInput(): void {
    setTimeout(() => {
      if (this.inputField) {
        this.inputField.nativeElement.focus();
      }
    }, 100);
  }

  /**
   * Get parsed message content with doc links extracted.
   */
  getParsedMessage(message: ChatMessage): ParsedMessage {
    const cacheKey = message.timestamp.toString() + message.content;

    if (this.parsedMessagesCache.has(cacheKey)) {
      return this.parsedMessagesCache.get(cacheKey)!;
    }

    const parsed = this.parseMessage(message.content);
    this.parsedMessagesCache.set(cacheKey, parsed);
    return parsed;
  }

  /**
   * Parse message content: extract doc links and convert markdown to HTML.
   */
  private parseMessage(content: string): ParsedMessage {
    const docLinks: { title: string; url: string }[] = [];

    // Extract DOCS:[[Title|url]] format
    let textContent = content.replace(/DOCS:\[\[([^\]|]+)\|([^\]]+)\]\]/g, (_, title, url) => {
      docLinks.push({ title: title.trim(), url: url.trim() });
      return '';
    });

    // Also extract standard markdown links [Title](url) and convert to doc links
    textContent = textContent.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, (_, title, url) => {
      docLinks.push({ title: title.trim(), url: url.trim() });
      return '';
    });

    // Remove separator line (---) before doc links
    textContent = textContent.replace(/\n?---\s*$/gm, '');

    // Convert basic markdown to HTML
    let html = this.escapeHtml(textContent.trim());

    // Bold: **text** or __text__
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__([^_]+)__/g, '<strong>$1</strong>');

    // Convert numbered lists (1. item)
    html = html.replace(/^(\d+)\.\s+(.+)$/gm, '<li>$2</li>');
    html = html.replace(/(<li>.*<\/li>\n?)+/g, '<ol>$&</ol>');

    // Convert bullet lists (- item or * item)
    html = html.replace(/^[-*]\s+(.+)$/gm, '<li>$1</li>');
    html = html.replace(/(<li>.*<\/li>\n?)+/g, match => {
      if (!match.includes('<ol>')) {
        return '<ul>' + match + '</ul>';
      }
      return match;
    });

    // Convert paragraphs (double newlines)
    html = html.replace(/\n\n+/g, '</p><p>');
    html = '<p>' + html + '</p>';

    // Convert single newlines to breaks within paragraphs
    html = html.replace(/\n/g, '<br>');

    // Clean up empty paragraphs
    html = html.replace(/<p>\s*<\/p>/g, '');
    html = html.replace(/<p>\s*<br>\s*<\/p>/g, '');

    return {
      content: this.sanitizer.bypassSecurityTrustHtml(html),
      docLinks
    };
  }

  /**
   * Escape HTML special characters.
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Open a documentation link in a new tab.
   */
  openDocLink(url: string): void {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
